/**
 * Un choix parmi quelques valeurs, stocké dans la query string.
 *
 * Même mécanique que `useComparePref` et le sélecteur d'analyse, généralisée :
 * le choix survit au rechargement, l'URL reste partageable, et la valeur par
 * défaut n'apparaît jamais dans l'URL.
 */

import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

export function useSearchChoice<T extends string>(
  param: string,
  values: readonly T[],
  fallback: T,
): [T, (next: T) => void] {
  const [searchParams, setSearchParams] = useSearchParams();

  const raw = searchParams.get(param);
  // Une valeur bricolée à la main retombe sur le défaut plutôt que de partir
  // telle quelle vers l'API.
  const current = values.includes(raw as T) ? (raw as T) : fallback;

  const select = useCallback(
    (next: T) => {
      const params = new URLSearchParams(searchParams);
      if (next === fallback) params.delete(param);
      else params.set(param, next);
      // `replace` : basculer une option ne doit pas empiler d'historique.
      setSearchParams(params, { replace: true });
    },
    [param, fallback, searchParams, setSearchParams],
  );

  return [current, select];
}
